import {DeepReadonly} from '@raison/types';
import {type} from 'arktype';
import React, {useEffect, useMemo, useRef} from 'react';
import {createSearchParams, NavigateFunction, NavigateOptions, SetURLSearchParams, useLocation} from 'react-router';
import {parse, stringify} from 'zipson';
import {useNonReactiveCallback} from '~/library/ui-kit';

const occupiedNamespaces = new Set<string>();

export function useInHistoryStateNamespace(namespace: string, navigate: NavigateFunction) {
    useEffect(() => {
        if (occupiedNamespaces.has(namespace)) {
            throw new Error(`InHistoryState under "${namespace}" is occupied`);
        }
        occupiedNamespaces.add(namespace);
        return () => {
            occupiedNamespaces.delete(namespace);
        };
    }, [namespace]);

    const callsCount = useRef(0);
    callsCount.current = 0;

    function useBoundInHistoryState<T>(parser: Parser<T>, defaultState: T): [T, SetInHistoryState<DeepReadonly<T>>];
    function useBoundInHistoryState<T>(
        parser: Parser<T>
    ): [T | undefined, SetInHistoryState<DeepReadonly<T> | undefined>];
    function useBoundInHistoryState<T>(
        parser: Parser<T>,
        defaultState?: T
    ): [T | undefined, SetInHistoryState<DeepReadonly<T> | undefined>] {
        let localNamespace = namespace;
        if (callsCount.current !== 0) {
            localNamespace = `${namespace}[${callsCount.current}]`;
        }
        callsCount.current += 1;
        return useInHistoryStateImpl(navigate, localNamespace, parser, defaultState);
    }

    return useBoundInHistoryState;
}

type SetInHistoryState<T> = (update: React.SetStateAction<T>, options?: NavigateOptions) => void;

type Parser<T> = {
    (thing: unknown): T | type.errors;
};

function useInHistoryStateImpl<T>(
    navigate: NavigateFunction,
    namespace: string,
    parser: Parser<T>,
    fallback?: T
): [T | undefined, SetInHistoryState<DeepReadonly<T> | undefined>] {
    const encodeToSearch = stringify;
    const decodeFromSearch = parse;
    // const encodeToSearch = identity;
    // const decodeFromSearch = identity;

    const [params, setParams] = useSearchParams(navigate);

    const paramValueRaw = params.get(namespace);
    const paramValue: T | undefined = useMemo(() => parseParam(paramValueRaw), [paramValueRaw]);

    function parseParam(paramValueRaw: string | undefined | null): T | undefined {
        if (!paramValueRaw) {
            return undefined;
        }
        const parsed = parser(decodeFromSearch(paramValueRaw));
        if (parsed instanceof type.errors) {
            return undefined;
        } else {
            return parsed;
        }
    }

    const setState = useNonReactiveCallback(
        (update: React.SetStateAction<DeepReadonly<T> | undefined>, options?: NavigateOptions) => {
            setParams(prev => {
                if (update instanceof Function) {
                    update = update((parseParam(prev.get(namespace)) ?? fallback) as DeepReadonly<T>);
                }
                prev.set(namespace, encodeToSearch(update));
                return prev;
            }, options);
        }
    );

    return [paramValue ?? fallback, setState];
}

export function useSearchParams(navigate: NavigateFunction): [URLSearchParams, SetURLSearchParams] {
    const location = useLocation();
    const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);

    const setSearchParams: SetURLSearchParams = useNonReactiveCallback((nextInit, navigateOptions) => {
        if (nextInit instanceof Function) {
            nextInit = nextInit(new URLSearchParams(searchParams));
        }
        navigate('?' + createSearchParams(nextInit), navigateOptions);
    });

    return [searchParams, setSearchParams];
}
